import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

/** 오프스크린 스테이지 안의 이미지가 모두 로드될 때까지 기다립니다. */
async function waitForImages(root: HTMLElement): Promise<void> {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  );
}

function sanitizeFileName(label: string): string {
  const cleaned = label.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim();
  // 파일명이 너무 길어지면 일부 OS에서 저장이 실패할 수 있어 적당히 잘라냅니다.
  const base = cleaned.length > 80 ? `${cleaned.slice(0, 80).trim()}…` : cleaned;
  const stamp = new Date().toISOString().slice(0, 10);
  return `${base || '오답노트'}_${stamp}.pdf`;
}

/**
 * 오프스크린 스테이지에 그려진 오답노트 시트들을 캡처해서
 * A4 크기의 한 개 PDF 파일로 묶어 다운로드합니다.
 *
 * @returns 내보낸 페이지(오답노트) 수
 */
export async function exportNotesToPdf(stage: HTMLElement, label: string): Promise<number> {
  const sheets = Array.from(stage.querySelectorAll<HTMLElement>('[data-pdf-sheet]'));
  if (sheets.length === 0) throw new Error('내보낼 오답노트가 없어요.');

  if (typeof document.fonts !== 'undefined' && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // 폰트 로딩 실패는 무시하고 진행합니다.
    }
  }

  await waitForImages(stage);

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });

  for (let i = 0; i < sheets.length; i += 1) {
    // 캡처는 순차적으로 진행합니다(메모리 사용을 줄이기 위함).
    // eslint-disable-next-line no-await-in-loop
    const canvas = await html2canvas(sheets[i], {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
      ignoreElements: (el) => el instanceof HTMLElement && el.classList.contains('print-hide'),
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    if (i > 0) pdf.addPage();

    const ratio = canvas.width / canvas.height;
    let renderWidth = A4_WIDTH_MM;
    let renderHeight = A4_WIDTH_MM / ratio;
    if (renderHeight > A4_HEIGHT_MM) {
      renderHeight = A4_HEIGHT_MM;
      renderWidth = A4_HEIGHT_MM * ratio;
    }
    const offsetX = (A4_WIDTH_MM - renderWidth) / 2;
    const offsetY = (A4_HEIGHT_MM - renderHeight) / 2;

    pdf.addImage(imgData, 'JPEG', offsetX, offsetY, renderWidth, renderHeight);
  }

  pdf.save(sanitizeFileName(label));
  return sheets.length;
}